import React, { useState, useEffect } from "react";

const DEFAULT_ROWS = 1;

const AttachmentUpload = ({ values, setValues, errors = {} }) => {
    const [rowCount, setRowCount] = useState(DEFAULT_ROWS);
    const [openNotesRow, setOpenNotesRow] = useState(null);

    const rawAttachments = values?.attachments || [];
    const attachments = rawAttachments.map((a, i) => ({
        rowId: a.rowId ?? i,
        ...a,
    }));

    useEffect(() => {
        if (attachments.length > rowCount) {
            setRowCount(attachments.length);
        }
    }, [values?.id]);

    useEffect(() => {
        const lpo = attachments.find((a) => a.rowId === 0);
        if (lpo?.attachmentUrl && !lpo?.documentName) {
            setAttachmentState(0, { documentName: "LPO" });
        }
        attachments.forEach((a) => {
            if (a.rowId !== 0 && a.attachmentUrl && !a.documentName) {
                setAttachmentState(a.rowId, { documentName: `Other Document ${a.rowId}` });
            }
        });
    }, [values?.id]);

    const resolveAttachmentUrl = async (file) => file.name;

    const setAttachmentState = (rowId, patch) => {
        setValues((prev) => {
            const existing = (prev?.attachments || []).map((a, i) => ({
                rowId: a.rowId ?? i,
                ...a,
            }));
            const idx = existing.findIndex((a) => a.rowId === rowId);
            const base = idx > -1 ? existing[idx] : { rowId };
            const updatedEntry = {
                ...base,
                attachmentTypeId: rowId === 0 ? 1 : 2,
                documentName: base.documentName || patch.documentName || (rowId === 0 ? "LPO" : ""),
                ...patch,
            };
            const updated =
                idx > -1
                    ? existing.map((a, i) => (i === idx ? updatedEntry : a))
                    : [...existing, updatedEntry];
            return { ...prev, attachments: updated };
        });
    };

    const handleFileChange = async (e, rowId) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const url = await resolveAttachmentUrl(file);
        setAttachmentState(rowId, {
            attachmentName: file.name,
            attachmentUrl: url,
            fileObj: file,
        });
    };

    const handleRemoveFile = (rowId) => {
        setAttachmentState(rowId, { attachmentName: "", attachmentUrl: "", fileObj: null });
    };

    const handleNotesSave = (rowId, notes) => {
        setAttachmentState(rowId, { notes });
        setOpenNotesRow(null);
    };

    const handleRemoveRow = (rowId) => {
        setRowCount((prev) => Math.max(prev - 1, DEFAULT_ROWS));
        setValues((prev) => ({
            ...prev,
            attachments: (prev?.attachments || []).filter((a, i) => (a.rowId ?? i) !== rowId),
        }));
    };

    const handleAddRow = () => setRowCount((prev) => prev + 1);

    const rows = Array.from({ length: rowCount }, (_, i) => i);

    return (
        <div className="attach-list">
            {rows.map((rowId) => {
                const attachment = attachments.find((a) => a.rowId === rowId);
                const hasFile = !!attachment?.attachmentName;
                const hasNotes = !!attachment?.notes;
                const notesOpen = openNotesRow === rowId;

                return (
                    <div className="attach-row" key={rowId}>
                        <div className="attach-row-main justify-content-between">
                            <div className="d-flex align-items-center">
                                <div className="attach-index mr-2">{rowId + 1}</div>
                                <div className="attach-label">
                                    {rowId === 0 ? (
                                        <>LPO <span className="mandatory">*</span></>
                                    ) : (
                                        <input
                                            type="text"
                                            className="attach-label-input"
                                            placeholder={`Other Document ${rowId}`}
                                            value={attachment?.documentName ?? ""}
                                            onChange={(e) =>
                                                setAttachmentState(rowId, { documentName: e.target.value })
                                            }
                                        />
                                    )}
                                </div>
                            </div>
                            <div className="d-flex align-items-center">
                                <div className="attach-file mr-2">
                                    {hasFile ? (
                                        <div className="attach-file-chip">
                                            <i className="far fa-file-pdf"></i>
                                            <span className="attach-file-name" title={attachment.attachmentName}>
                                                {attachment.attachmentName}
                                            </span>
                                            <i
                                                className="fas fa-times"
                                                onClick={() => handleRemoveFile(rowId)}
                                            ></i>
                                        </div>
                                    ) : (
                                        <label className="attach-file-btn m-0" htmlFor={`fileUpload-${rowId}`}>
                                            <span className="material-icons-outlined">file_upload</span>
                                            <span>Attach File</span>
                                            <input
                                                id={`fileUpload-${rowId}`}
                                                type="file"
                                                accept=".pdf"
                                                onChange={(e) => handleFileChange(e, rowId)}
                                                onClick={(e) => { e.target.value = null; }}
                                            />
                                        </label>
                                    )}
                                </div>

                                <button
                                    type="button"
                                    className={`attach-notes-btn${hasNotes ? " has-notes" : ""}${notesOpen ? " is-open" : ""}`}
                                    onClick={() => setOpenNotesRow(notesOpen ? null : rowId)}
                                >
                                    <i className="far fa-file-alt"></i>
                                    <span>{hasNotes ? "Notes added" : "Add Notes"}</span>
                                </button>
                            </div>

                            {rowId !== 0 && (
                                <i
                                    className="fas fa-trash-alt attach-delete"
                                    onClick={() => handleRemoveRow(rowId)}
                                ></i>
                            )}
                        </div>

                        {errors?.[`attachment_${rowId}`] && (
                            <p className="form-error">{errors[`attachment_${rowId}`]}</p>
                        )}

                        {notesOpen && (
                            <div className="attach-notes-panel">
                                <textarea
                                    className="form-control"
                                    placeholder="Enter notes here"
                                    rows="3"
                                    defaultValue={attachment?.notes || ""}
                                    id={`notes-${rowId}`}
                                ></textarea>
                                <div className="attach-notes-actions">
                                    <button
                                        type="button"
                                        className="btn cancel-btn"
                                        onClick={() => setOpenNotesRow(null)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn save-btn"
                                        onClick={() => {
                                            const val = document.getElementById(`notes-${rowId}`)?.value;
                                            handleNotesSave(rowId, val);
                                        }}
                                    >
                                        Save
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}

            <button type="button" className="attach-add-btn" onClick={handleAddRow}>
                <i className="fas fa-plus"></i> Add Attachment
            </button>
        </div>
    );
};

export default AttachmentUpload;